import React from 'react';
import {gsap} from 'gsap';
import {ScrollToPlugin} from 'gsap/ScrollToPlugin';
import * as R from 'ramda';
import {Image} from '@shopify/hydrogen';

//domains
import type {Brand as BrandType} from 'domains/brands';
import type {BrandsSetting} from 'domains/brands';
//components
import {Link} from 'components/Link';
import Container from 'components/Container';
import SanityImage from 'components/SanityImage';
import {useLoaderData} from '@remix-run/react';

//data
import {loader} from '~/routes/($lang)/pages/$pageHandle';
import {brandHref} from '~/lib/brandCollections';
gsap.registerPlugin(ScrollToPlugin);
export default function Brand() {
  const {title, allBrands, vendorBrands, brandsSettings} =
    useLoaderData<typeof loader>();

  // The A-Z directory follows Shopify live (every vendor with in-stock
  // product, computed in the loader). Sanity's hand-maintained brand docs
  // are only the fallback when that scan failed, and still supply the
  // featured-logos strip below the header.
  const directory: {title: string; link: string}[] =
    vendorBrands && vendorBrands.length > 0
      ? vendorBrands.map((vendor) => ({
          title: vendor,
          link: brandHref(vendor),
        }))
      : (allBrands ?? []).map((b) => ({
          title: b.title,
          link: brandHref(b.shopify_brand_node ?? b.title),
        }));

  const alpha = Array.from(Array(26)).map((e, i) => i + 65);
  const alphabets = alpha.map((x) => String.fromCharCode(x));
  const availableAlphabets = alphabets.filter((a) =>
    directory
      .map((b) => b.title.toUpperCase()?.replace(/\s/g, '').charAt(0))
      .includes(a),
  );
  const brandsByInitials = [
    {
      initials: '#',
      brands: directory.filter(
        (b) =>
          !availableAlphabets.includes(
            b?.title?.replace(/\s/g, '')?.charAt(0)?.toUpperCase(),
          ),
      ),
    },
  ]
    .concat(
      availableAlphabets.map((a) => {
        return {
          initials: a,
          brands: directory.filter(
            (b) =>
              b?.title?.replace(/\s/g, '')?.charAt(0)?.toLocaleLowerCase() ===
              a.toLocaleLowerCase(),
          ),
        };
      }),
    )
    .filter((b) => b.brands.length > 0);
  return (
    <section className="pb-20">
      <div className="">
        <SanityImage
          layout="responsive"
          sizes={['50vw, 100vw']}
          src={brandsSettings?.banner?.asset?._ref}
          alt={(brandsSettings?.banner as {alt?: string})?.alt ?? ''}
          className="h-full object-cover"
        />
      </div>
      <Container>
        <div className="mx-auto w-full md:w-10/12">
          <header className="mx-auto w-10/12 -translate-y-1/2 border border-hk-gray bg-white text-center shadow-md md:w-1/2">
            <div className="py-6 px-4 md:py-14 md:px-10">
              <h1 className="mb-4 text-[22px] md:mb-6 md:text-[42px]">
                {brandsSettings?.title || title}
              </h1>
              <p className="text-xs md:text-base">
                {brandsSettings?.description}
              </p>
            </div>
          </header>
          <div className="flex items-center justify-between overflow-x-scroll border-b border-hk-deep-gray md:space-x-20 md:overflow-hidden md:pb-4">
            <div className="hidden text-[26px] font-normal text-slate-400 md:block">
              Go to:
            </div>
            <ul className="flex flex-1 items-center justify-between space-x-4 pb-4 md:flex-wrap md:space-x-1 md:pb-0">
              <li className="">
                <button
                  className="text-base font-semibold md:hidden md:text-[20px] md:font-normal"
                  onClick={() => {
                    gsap.to(window, {
                      duration: 1.4,
                      scrollTo: {y: `#brand-#`, offsetY: 105},
                      ease: 'power2.out',
                    });
                  }}
                >
                  0-9
                </button>
                <button
                  className="hidden text-base font-semibold md:block md:text-[20px] md:font-normal"
                  onClick={() => {
                    gsap.to(window, {
                      duration: 1.4,
                      scrollTo: {y: `#brand-#`, offsetY: 175},
                      ease: 'power2.out',
                    });
                  }}
                >
                  0-9
                </button>
              </li>
              {availableAlphabets.map((a) => (
                <li key={`brand-${a}`} className={`brand-${a}`}>
                  <button
                    className="text-base font-semibold uppercase md:hidden md:text-[20px] md:font-normal"
                    onClick={() => {
                      gsap.to(window, {
                        duration: 1.4,
                        scrollTo: {y: `#brand-${a}`, offsetY: 105},
                        ease: 'power2.out',
                      });
                    }}
                  >
                    {a}
                  </button>
                  <button
                    className="hidden text-base font-semibold uppercase md:block md:text-[20px] md:font-normal"
                    onClick={() => {
                      gsap.to(window, {
                        duration: 1.4,
                        scrollTo: {y: `#brand-${a}`, offsetY: 175},
                        ease: 'power2.out',
                      });
                    }}
                  >
                    {a}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="">
            <ul className="flex grid-cols-5 space-x-4 overflow-x-scroll py-4 text-center md:grid md:gap-8 md:space-x-0 md:overflow-hidden md:py-10">
              {brandsSettings?.featured_brands &&
                brandsSettings?.featured_brands.map((b) => {
                  if (b.brand_logo) {
                    return (
                      <li key={`fb-${b.title}`} className="flex items-center">
                        <Link
                          to={brandHref(b.shopify_brand_node ?? b.title)}
                          className="w-full block"
                        >
                          <SanityImage
                            layout="responsive"
                            sizes={['50vw, 100vw']}
                            src={b.brand_logo?.asset._ref}
                            alt={b.title ?? ''}
                            loading="lazy"
                            className="h-full object-contain"
                          />
                        </Link>
                      </li>
                    );
                  }
                  if (b?.shopify_brand?.shopify_collection?.store?.imageUrl) {
                    return (
                      <li key={`fb-${b.title}`} className="flex items-center">
                        <Link
                          to={brandHref(b.shopify_brand_node ?? b.title)}
                          className="w-full block"
                        >
                          <Image
                            data={{
                              url: b.shopify_brand.shopify_collection.store
                                .imageUrl,
                            }}
                            width={400}
                            height={160}
                            alt={b.title}
                          />
                        </Link>
                      </li>
                    );
                  }
                  return <></>;
                })}
            </ul>
          </div>
          <div className="">
            {brandsByInitials.map((b) => {
              return (
                <div
                  id={`brand-${b.initials}`}
                  key={`brands-start-with-${b.initials}`}
                  className="flex  border-b pb-6 pt-4"
                >
                  <div className="text-14 mr-4 font-bold md:mr-0 md:w-2/12 md:text-[20px]">
                    {b.initials}
                  </div>
                  <ul className="grid flex-1 grid-cols-3 gap-y-4">
                    {b.brands.map((brand) => (
                      <li
                        key={`brand-${brand.title}`}
                        className="text-sm font-normal uppercase md:text-[22px]"
                      >
                        <Link to={brand.link}>{brand.title}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </Container>
    </section>
  );
}
